import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Play, ArrowRight, Check, Gift, Download, Mail } from 'lucide-react';
import { api } from '../services/api.js';

const CourseLanding = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Store email for course enrollment
      localStorage.setItem('courseEmail', email);
      
      // Redirect to course page or dashboard
      // You can customize this to redirect to a course page or register flow
      navigate('/register?source=course');
    } catch (error) {
      console.error('Error submitting email:', error);
      // Still redirect even if there's an error
      navigate('/register?source=course');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Top Section */}
      <section className="py-12 lg:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto text-center">
          {/* Main Headline */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-white">
            FREE 10 Part Video Course
            <span className="block text-yellow-400 mt-2">(Save $997)</span>
          </h1>

          {/* Sub-headline */}
          <p className="text-xl sm:text-2xl lg:text-3xl mb-4 text-gray-100 leading-relaxed">
            My Proven Strategy To <span className="text-yellow-400 font-bold">Transform Your Fitness After 50</span> With Gentle, 
            Safe Exercises That <span className="text-yellow-400 font-bold">Reduce Pain, Build Strength, and Restore Confidence</span> 
            - All From The Comfort Of Your Home.
          </p>

          {/* Disclaimer */}
          <p className="text-sm sm:text-base text-gray-400 mb-8 italic">
            (Without expensive gym memberships, complicated equipment, or risking injury)
          </p>

          {/* CTA Button 1 */}
          <form onSubmit={handleSubmit} className="max-w-md mx-auto mb-8">
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="flex-grow px-6 py-4 rounded-lg text-gray-900 text-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-bold px-8 py-4 rounded-lg text-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isSubmitting ? (
                  'Enrolling...'
                ) : (
                  <>
                    Enroll In The FREE 10 Part Course
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </div>
          </form>

          {/* Trust Indicators */}
          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-400 mb-8">
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-400" />
              <span>No credit card required</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-400" />
              <span>Instant access</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-400" />
              <span>100% free</span>
            </div>
          </div>
        </div>
      </section>

      {/* Middle Section - Visual/Graphic */}
      <section className="py-8 px-4 sm:px-6 lg:px-8 bg-gray-800">
        <div className="max-w-6xl mx-auto">
          <div className="bg-gradient-to-br from-gray-700 to-gray-800 rounded-2xl p-8 lg:p-12 border border-gray-600">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              {/* Left side - Course preview */}
              <div>
                <h2 className="text-3xl font-bold mb-6 text-yellow-400">
                  What You'll Learn:
                </h2>
                <ul className="space-y-4 text-lg">
                  <li className="flex items-start gap-3">
                    <Play className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
                    <span><strong className="text-yellow-400">Day 1:</strong> Fix Bad Knees - Gentle Low Impact Exercises</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Play className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
                    <span><strong className="text-yellow-400">Day 2:</strong> Stop Falls - Best Balance Exercises</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Play className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
                    <span><strong className="text-yellow-400">Day 3:</strong> Building Muscle After 50</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Play className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
                    <span><strong className="text-yellow-400">Day 4:</strong> Fight Fatigue - Low-Impact Cardio</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Play className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
                    <span><strong className="text-yellow-400">Day 5:</strong> Lose Belly Fat - Hormonal Weight Gain</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Play className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
                    <span className="text-gray-300">+ 5 more essential topics...</span>
                  </li>
                </ul>
              </div>

              {/* Right side - Visual representation */}
              <div className="relative">
                <div className="bg-gray-900 rounded-xl p-6 border border-gray-600">
                  <div className="aspect-video bg-gradient-to-br from-indigo-600 to-purple-600 rounded-lg flex items-center justify-center mb-4">
                    <Play className="w-16 h-16 text-white" />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                      <div
                        key={num}
                        className="aspect-square bg-gradient-to-br from-yellow-400 to-orange-400 rounded flex items-center justify-center text-gray-900 font-bold text-sm"
                      >
                        {num}
                      </div>
                    ))}
                  </div>
                  <p className="text-center text-gray-400 mt-4 text-sm">
                    10 Video Series
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom Section - Giveaway Offer */}
      <section className="py-12 lg:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto text-center">
          {/* Giveaway Headline */}
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 text-yellow-400">
            TODAY ONLY: Plus Get My Complete Fitness Guide
            <span className="block text-white mt-2 text-2xl sm:text-3xl">(Save $997)</span>
          </h2>

          {/* Giveaway Description */}
          <div className="bg-gray-800 rounded-xl p-8 mb-8 border border-gray-700">
            <div className="flex items-center justify-center mb-6">
              <Gift className="w-16 h-16 text-yellow-400" />
            </div>
            <h3 className="text-2xl font-bold mb-4 text-white">
              "The Complete Over-50 Fitness Starter Guide"
            </h3>
            <p className="text-lg text-gray-300 mb-6">
              When you enroll in the free course, you'll also receive instant access to:
            </p>
            <ul className="text-left max-w-2xl mx-auto space-y-3 text-gray-200">
              <li className="flex items-start gap-3">
                <Check className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                <span><strong>30-Day Exercise Plan</strong> - Step-by-step routines for each week</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                <span><strong>Exercise Form Checklist</strong> - Ensure you're doing each move safely</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                <span><strong>Progress Tracking Sheets</strong> - Monitor your strength and mobility gains</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                <span><strong>Safety Guidelines PDF</strong> - Know when to modify or rest</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                <span><strong>Nutrition Tips for Active Seniors</strong> - Fuel your body right</span>
              </li>
            </ul>
          </div>

          {/* CTA Prompt */}
          <p className="text-xl mb-6 text-gray-200">
            Click The Button Below To Enroll In The FREE Course And Get Your Bonus Guide...
          </p>

          {/* CTA Button 2 */}
          <form onSubmit={handleSubmit} className="max-w-md mx-auto mb-8">
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="flex-grow px-6 py-4 rounded-lg text-gray-900 text-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-bold px-8 py-4 rounded-lg text-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isSubmitting ? (
                  'Unlocking...'
                ) : (
                  <>
                    Unlock Free Course + Bonus Guide
                    <Download className="w-5 h-5" />
                  </>
                )}
              </button>
            </div>
          </form>

          {/* Opt-Out Alternative */}
          <p className="text-sm text-gray-500 mt-8 italic max-w-2xl mx-auto">
            No Thanks! I don't want free fitness guidance and I'm happy to keep struggling with joint pain, 
            balance issues, and low energy, even though I know that staying inactive only makes things worse as I age.
          </p>

          {/* Additional Trust Elements */}
          <div className="mt-12 pt-8 border-t border-gray-700">
            <p className="text-gray-400 mb-4">Created by Dr. Peter Murray</p>
            <p className="text-sm text-gray-500">
              Founder of ElderFit Secrets • Physical Therapist • Over 50 Fitness Specialist
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CourseLanding;




